import { useCallback, useMemo } from 'react';
import { useUrlString } from './useUrlState';

export type SortState = { key: string; dir: 'asc' | 'desc' } | null;

interface UseServerSortOptions {
  /**
   * Query-string key the sort is kept under. Defaults to `sort`; give a second sortable table on the
   * same page its own key so the two do not fight over one param.
   */
  urlKey?: string;
  /**
   * Optional map from a column's `key`/`dataIndex` (what the table reports) to the backend sort
   * field name, when they differ (e.g. column key `role` → backend field `role.code`). Keys not in
   * the map are passed through unchanged.
   */
  fieldMap?: Record<string, string>;
  /** Initial sort, e.g. `{ key: 'createdAt', dir: 'desc' }`. */
  defaultSort?: SortState;
  /** Called whenever the sort changes — typically `() => setPage(1)` so paging restarts. */
  onChange?: (next: SortState) => void;
}

/**
 * Server-side table sorting glue. Wire a paginated list table for backend `ORDER BY`:
 *
 *   const { sortState, onSortChange, sort } = useServerSort({ onChange: () => setPage(1) });
 *   // params: { ...filters, ...(sort ? { sort } : {}) }
 *   // <DataTable sortState={sortState} onSortChange={onSortChange} … />
 *
 * `sort` is the query-string value using the shared `-field` convention (leading `-` = descending),
 * e.g. `name` (asc) or `-createdAt` (desc), or `undefined` when nothing is sorted. The backend
 * `applySort` helper parses that same convention against a per-endpoint whitelist.
 */
export function useServerSort(opts: UseServerSortOptions = {}) {
  const { fieldMap, defaultSort = null, onChange, urlKey = 'sort' } = opts;

  /*
   * The sort lives in the URL for the same reason the filters do: a `useState` sort is lost the
   * moment you open a row and come back, so a list you had ordered by oldest-first silently reverts
   * to the default and the row you were working through is somewhere else on the page.
   *
   * What is stored is the COLUMN key (`-createdAt`), not the mapped backend field, so `fieldMap` stays
   * an implementation detail of the request rather than something baked into shareable URLs.
   */
  const encode = (s: SortState) => (s ? (s.dir === 'desc' ? `-${s.key}` : s.key) : '');
  const [raw, setRaw] = useUrlString(urlKey, encode(defaultSort));

  const sortState = useMemo<SortState>(() => {
    if (!raw) return null;
    return raw.startsWith('-') ? { key: raw.slice(1), dir: 'desc' } : { key: raw, dir: 'asc' };
  }, [raw]);

  /*
   * A DEFAULTED COLUMN'S HEADER WAS DEAD.
   *
   * The table's sort cycle is asc → desc → OFF, and "off" is written as an empty param — which this
   * hook's fallback then reads as "unset" and answers with the DEFAULT sort. On any other column
   * that is exactly right: the third click resets the table. On the column that CARRIES the default
   * it was a loop with no exit — "Decided on" opens at desc, one click asks for off, off resolves
   * back to the default desc, and the header could never reach ascending. The oldest decision on the
   * page was unreachable, and the arrow never moved, so the table looked broken (it was).
   *
   * A table with a default order has no meaningful "unsorted" state for its own default column, so
   * clearing THAT column flips it instead and the header keeps toggling asc ↔ desc. Clearing any
   * other column still returns to the default.
   */
  const onSortChange = useCallback((next: SortState) => {
    const resolved: SortState = (next == null && defaultSort && sortState?.key === defaultSort.key)
      ? { key: defaultSort.key, dir: sortState.dir === 'asc' ? 'desc' : 'asc' }
      : next;
    setRaw(encode(resolved));
    onChange?.(resolved);
  }, [onChange, setRaw, defaultSort, sortState]);

  const sort = useMemo(() => {
    if (!sortState) return undefined;
    const field = fieldMap?.[sortState.key] ?? sortState.key;
    return sortState.dir === 'desc' ? `-${field}` : field;
  }, [sortState, fieldMap]);

  return { sortState, onSortChange, sort };
}
